/**
 * Authentication state management using Zustand.
 *
 * Token storage is in-memory only (via tokenManager). No localStorage
 * or sessionStorage persistence — session continuity is provided by
 * the httpOnly refresh cookie.
 *
 * (Monday Item ID: 11501542831)
 */

import { create } from 'zustand';
import { api } from '@/lib/api/endpoints';
import { tokenManager } from '@/services/tokenManager';

interface User {
  id: string;
  username: string;
  email: string;
  roles: string[];
  is_superuser: boolean;
}

interface AuthState {
  // State
  user: User | null;
  isAuthenticated: boolean;
  isInitializing: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  initializeAuth: () => Promise<void>;
  setUser: (user: User | null) => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()((set, _get) => ({
  // Initial state
  user: null,
  isAuthenticated: false,
  isInitializing: true,
  isLoading: false,
  error: null,

  // Login action
  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.auth.login({ email, password });
      const { access_token, user } = response.data;

      // Store token in memory via tokenManager (not localStorage)
      tokenManager.setInitialTokens(access_token);

      set({
        user,
        isAuthenticated: true,
        isInitializing: false,
        isLoading: false,
        error: null,
      });
    } catch (error: any) {
      set({
        error: error.message || 'Login failed',
        isLoading: false,
        isAuthenticated: false,
      });
      throw error;
    }
  },

  // Logout action
  logout: async () => {
    set({ isLoading: true });
    try {
      // tokenManager.clearTokens() calls the logout endpoint
      tokenManager.clearTokens();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  },

  // Initialize auth from refresh cookie (called on app mount)
  initializeAuth: async () => {
    const authenticated = await tokenManager.initialize();
    if (authenticated) {
      const payload = tokenManager.getTokenPayload();
      if (payload) {
        set({
          user: {
            id: payload.sub,
            username: payload.username || payload.email?.split('@')[0] || '',
            email: payload.email || '',
            roles: payload.roles || [],
            is_superuser: payload.is_superuser ?? false,
          },
          isAuthenticated: true,
          isInitializing: false,
        });
        return;
      }
    }
    set({ isAuthenticated: authenticated, isInitializing: false });
  },

  // Set user (useful for updating user info after profile edits)
  setUser: (user: User | null) => {
    set({ user });
  },

  // Clear error
  clearError: () => {
    set({ error: null });
  },
}));

// Subscribe to token refresh events — keep isAuthenticated in sync
tokenManager.onTokenRefresh(() => {
  useAuthStore.setState({ isAuthenticated: true });
});
